import type { BibleBookmark } from '@/types/bookmark';
import { getContentOwnerBinding } from '@/lib/content-owner';
import {
  bookmarkFromCompleteDocument,
  completeBookmarkDocument,
} from '@/lib/content-lifecycle/contracts';
import {
  authorBookmarkCreateOnlyMutation,
  authorBookmarkNoteMutation,
  authorDeleteMutation,
  CONTENT_LIFECYCLE_GENERATION,
} from '@/lib/content-lifecycle/store';
import { syncContentLifecycle, type ContentSyncResult } from '@/lib/content-lifecycle/sync';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { getAuthEpoch, getAuthUserId } from '@/lib/keepsake/epochs';
import type { RecoveredAudioCommitClaim } from '@/types/audio-recovery';

/** Compatibility facade: all three content types share one lifecycle sync. */
export async function syncBookmarks(): Promise<ContentSyncResult> {
  if (isCoreReadOnly()) {
    return { pushed: 0, replayed: 0, pulled: 0, conflicts: 0, held: false };
  }
  return syncContentLifecycle();
}

export async function pushBookmark(bookmark: BibleBookmark): Promise<boolean> {
  if (!(await queueBookmarkMutation(bookmark))) return false;
  await syncContentLifecycle();
  return true;
}

export async function queueBookmarkMutation(
  bookmark: BibleBookmark,
  recoveredAudio: readonly RecoveredAudioCommitClaim[] = [],
): Promise<boolean> {
  if (isCoreReadOnly()) return false;
  const ownerId = getAuthUserId();
  if (!ownerId || (await getContentOwnerBinding()) !== ownerId) {
    throw new Error('bookmark-owner-context-stale');
  }
  const context = {
    ownerId,
    authEpoch: getAuthEpoch(),
    generation: CONTENT_LIFECYCLE_GENERATION,
  };
  if (bookmark.deletedAt) {
    await authorDeleteMutation(context, 'bookmark', bookmark.id, bookmark.deletedAt);
  } else {
    await authorBookmarkCreateOnlyMutation(
      context,
      completeBookmarkDocument(bookmark),
      recoveredAudio,
    );
  }
  return true;
}

export async function queueBookmarkNoteMutation(
  fallback: BibleBookmark,
  note: string,
  updatedAt: string,
  expectedUpdatedAt: string,
  recoveredAudio: readonly RecoveredAudioCommitClaim[] = [],
): Promise<BibleBookmark> {
  if (isCoreReadOnly()) throw new Error('bookmark-durable-commit-rejected');
  const ownerId = getAuthUserId();
  if (!ownerId || (await getContentOwnerBinding()) !== ownerId) {
    throw new Error('bookmark-owner-context-stale');
  }
  const context = {
    ownerId,
    authEpoch: getAuthEpoch(),
    generation: CONTENT_LIFECYCLE_GENERATION,
  };
  const document = await authorBookmarkNoteMutation(
    context,
    completeBookmarkDocument(fallback),
    note,
    updatedAt,
    expectedUpdatedAt,
    recoveredAudio,
  );
  return bookmarkFromCompleteDocument(document);
}

export function requestBookmarkLifecycleSync(): void {
  void syncContentLifecycle().catch(() => {
    // The durable tuple is already committed; reconnect retries it.
  });
}

export function initBookmarkOutbox(): void {
  if (isCoreReadOnly()) return;
  // initOutbox owns the one shared listener and awaited single-flight.
}
